// Standalone mockup — shows how #F7E7CE (Champagne) would look on the landing
// palette without touching the live site tokens. Delete once a decision is made.
import { ArrowUpRight, Apple, Sparkles } from "lucide-react";
import { Logo } from "@/components/landing/Logo";

const CHAMP = "#F7E7CE";
const CHAMP_DEEP = "#D9C29A"; // hover variant

export default function ChampagnePreview() {
  return (
    <div className="min-h-screen bg-[#F7F5F2] px-6 py-12 md:px-12 md:py-16">
      <div className="mx-auto max-w-[1200px]">
        <p className="mb-2 text-xs uppercase tracking-[0.28em] text-[#9a9a9a]">
          Colour preview
        </p>
        <h1 className="font-serif text-4xl font-light tracking-tight text-[#2A2724]">
          Champagne <span className="italic" style={{ color: CHAMP }}>#F7E7CE</span> on white
        </h1>
        <p className="mt-3 max-w-xl text-[#6B6B6B]">
          Same components, only the gold token has been swapped to champagne. Legibility
          notes are called out where the colour struggles.
        </p>

        {/* Header bar mock */}
        <div className="mt-10 rounded-2xl border border-black/10 bg-[#F7F5F2] p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Logo size={40} />
              <span className="font-serif text-xl font-semibold tracking-tight text-[#2A2724]">
                Book Away
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button className="inline-flex items-center gap-1.5 rounded-full border border-black/20 px-5 py-2 text-sm text-[#2A2724]">
                I'm a Business
                <ArrowUpRight className="h-4 w-4" />
              </button>
              <button
                className="rounded-full px-5 py-2 text-sm font-medium text-black"
                style={{ backgroundColor: CHAMP }}
              >
                I'm a Customer
              </button>
            </div>
          </div>
        </div>

        {/* Hero-style headline + buttons */}
        <div className="mt-12">
          <p
            className="text-[0.72rem] uppercase tracking-[0.28em]"
            style={{ color: CHAMP_DEEP }}
          >
            Last-minute beauty & grooming
          </p>
          <p className="mt-1 text-xs text-red-500">
            ⚠ Overline in pure champagne (#F7E7CE) fails on white — using deeper #D9C29A here
            for legibility.
          </p>
          <h2 className="mt-4 font-serif text-6xl font-light leading-[0.9] tracking-tighter text-[#2A2724]">
            Book <span className="italic" style={{ color: CHAMP }}>Away</span>
          </h2>
          <p className="mt-2 text-xs text-red-500">
            ⚠ "Away" in champagne on white has ~1.4:1 contrast — the letters fade
            into the page unless you punch it up (see deeper swatch below).
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              className="inline-flex items-center gap-3 rounded-full px-7 py-3.5 text-sm font-medium text-black"
              style={{ backgroundColor: CHAMP }}
            >
              <Apple className="h-4 w-4" />
              Download App
              <ArrowUpRight className="h-4 w-4" />
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-full border px-7 py-3.5 text-sm text-[#2A2724]"
              style={{ borderColor: CHAMP }}
            >
              I'm a Business
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Card row */}
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-black/10 bg-[#F7F5F2] p-6">
            <p
              className="text-[0.72rem] uppercase tracking-[0.28em]"
              style={{ color: CHAMP_DEEP }}
            >
              For Businesses
            </p>
            <p className="mt-4 font-serif text-2xl font-semibold leading-snug text-[#2A2724]">
              Turn empty slots into{" "}
              <span className="italic" style={{ color: CHAMP }}>revenue.</span>
            </p>
          </div>
          <div
            className="rounded-2xl p-6 text-black"
            style={{ backgroundColor: CHAMP }}
          >
            <p className="text-[0.72rem] uppercase tracking-[0.28em] text-black/60">
              For Customers
            </p>
            <p className="mt-4 font-serif text-2xl font-semibold leading-snug text-black">
              Book your look, <span className="italic">last minute.</span>
            </p>
          </div>
        </div>

        {/* Highlight tier card (dark) */}
        <div className="mt-12 max-w-md">
          <div className="rounded-3xl bg-[#2A2724] p-8 text-white">
            <span
              className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium"
              style={{ borderColor: `${CHAMP}80`, color: CHAMP, backgroundColor: `${CHAMP}1A` }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Best Value · Only 6 left
            </span>
            <h3 className="mt-6 font-serif text-3xl">Foundation</h3>
            <div className="mt-3 flex items-end gap-2">
              <span className="font-serif text-6xl leading-none" style={{ color: CHAMP }}>
                $49
              </span>
              <span className="mb-1 text-sm text-white/60">/mo</span>
            </div>
            <button
              className="mt-6 w-full rounded-full py-3 text-sm font-medium text-black"
              style={{ backgroundColor: CHAMP }}
            >
              Claim your Foundation spot
            </button>
          </div>
          <p className="mt-3 text-xs text-[#6B6B6B]">
            Champagne pops beautifully on the dark tier card — this is where it works best.
          </p>
        </div>

        {/* Verdict */}
        <div className="mt-14 rounded-2xl border border-black/10 bg-[#EDEAE4] p-6 text-sm leading-relaxed text-[#2A2724]">
          <p className="font-serif text-xl font-semibold">Honest read on Champagne #F7E7CE</p>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[#3a3a3a]">
            <li>Buttons on white: elegant and soft, but very low contrast with white text — black text only.</li>
            <li>
              Champagne <em>text</em> on a white page (overlines, italic "Away", price highlights) reads as almost
              invisible — WCAG contrast ~1.4:1.
            </li>
            <li>On dark surfaces (Foundation card, footer) it's stunning.</li>
            <li>
              Recommended: keep <span className="font-mono">#F7E7CE</span> as a decorative <em>surface</em> tone
              (button fills, chips) and pair with a deeper "champagne ink" like <span className="font-mono">#B8925A</span>{" "}
              for text so headings and highlights stay legible on white.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
